import { useMemo } from "react";
import Seo from "@/components/Seo";
import { LEGAL_DOCS } from "@/data/legalDocs";

const ROUTES = {
  privacy: "/privacy-policy",
  termsOfUse: "/terms-of-use",
  eula: "/eula",
  disclaimer: "/legal-disclaimer",
};

const LINK_RE = /(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)]|[\w.+-]+@[\w-]+\.[\w.]+)/g;
const LINK_CLASS =
  "text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-all break-all";

const linkify = (text, kp) =>
  text.split(LINK_RE).map((part, i) => {
    if (!part) return null;
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={`${kp}-${i}`} href={part} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          {part}
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(part)) {
      return (
        <a key={`${kp}-${i}`} href={`mailto:${part}`} className={LINK_CLASS}>
          {part}
        </a>
      );
    }
    return <span key={`${kp}-${i}`}>{part}</span>;
  });

const renderInline = (text, keyPrefix) => {
  const parts = text.split("**");
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={`${keyPrefix}-${i}`} className="text-white font-semibold">
        {linkify(part, `${keyPrefix}-${i}`)}
      </strong>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{linkify(part, `${keyPrefix}-${i}`)}</span>
    )
  );
};

const renderParagraph = (text, keyPrefix) =>
  text.split("\n").map((line, i) => (
    <span key={`${keyPrefix}-l${i}`}>
      {i > 0 && <br />}
      {renderInline(line, `${keyPrefix}-i${i}`)}
    </span>
  ));

export default function Legal({ doc }) {
  const path = ROUTES[doc];
  const data = LEGAL_DOCS[doc];
  const title = data?.title || "Legal";
  const intro = data?.intro || "";

  const toc = useMemo(
    () =>
      data
        ? data.blocks
            .map((b, i) =>
              b.t === "h" && /^\d+\.\s/.test(b.x) ? { id: `legal-section-${i}`, label: b.x } : null
            )
            .filter(Boolean)
        : [],
    [data]
  );

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="legal-page-container">
      <Seo title={`${title} — Tripsee`} description={intro} />

      <section className="relative pt-40 pb-14 overflow-hidden bg-radar-grid">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.3em] text-primary uppercase mb-6">
            tripsee.io{path}
          </p>
          <h1 data-testid="legal-doc-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
            {title}
          </h1>
          <p className="mt-5 text-lg text-slate-400 max-w-2xl">{intro}</p>
          {data?.meta?.length > 0 && (
            <div className="mt-7 flex flex-wrap gap-3">
              {data.meta.map((m) => (
                <span
                  key={m.label}
                  data-testid={`legal-meta-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/[0.07] px-4 py-2 text-sm"
                >
                  <span className="font-mono text-[10px] tracking-widest uppercase text-primary">{m.label}</span>
                  <span className="text-slate-200 font-medium">{m.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:grid xl:grid-cols-[260px_1fr] xl:gap-14">
          {toc.length > 1 && (
            <aside className="hidden xl:block">
              <div className="sticky top-28">
                <p className="font-mono text-[11px] tracking-[0.25em] text-slate-500 uppercase mb-5">
                  On this page
                </p>
                <nav className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-2">
                  {toc.map((t, i) => (
                    <button
                      key={t.id}
                      data-testid={`legal-toc-link-${i}`}
                      onClick={() => scrollTo(t.id)}
                      className="block w-full text-left text-[13px] leading-snug text-slate-400 hover:text-primary transition-colors py-1 border-l-2 border-white/10 hover:border-primary pl-3"
                    >
                      {t.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          <div className="max-w-3xl">
            {data?.blocks.map((b, i) => {
              if (b.t === "h") {
                const sub = b.x.match(/^(\d+\.\d+)\.?\s+(.+)$/);
                if (sub) {
                  return (
                    <h3
                      key={i}
                      data-testid={`legal-subsection-${i}`}
                      className="font-display text-base sm:text-lg font-bold text-slate-100 mt-8 mb-3 pl-5 border-l-2 border-primary/30"
                    >
                      <span className="text-primary/80 mr-2">{sub[1]}</span>
                      {sub[2]}
                    </h3>
                  );
                }
                const match = b.x.match(/^(\d+)\.\s+(.+)$/);
                return (
                  <h2
                    key={i}
                    id={`legal-section-${i}`}
                    data-testid={`legal-section-${i}`}
                    className="scroll-mt-28 font-display text-xl sm:text-2xl font-bold text-white mt-12 mb-4 first:mt-0"
                  >
                    {match ? (
                      <>
                        <span className="text-primary mr-2">{match[1]}</span>
                        {match[2]}
                      </>
                    ) : (
                      b.x
                    )}
                  </h2>
                );
              }
              if (b.t === "ul") {
                return (
                  <ul key={i} className="my-5 space-y-3">
                    {b.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-slate-300 leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        <span>{renderInline(item, `b${i}-${j}`)}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="my-5 text-slate-300 leading-relaxed">
                  {renderParagraph(b.x, `p${i}`)}
                </p>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
